---
title: Build a product page
description: Everything a product page needs, from the product and its variants to live stock, rails and SEO tags.
order: 4
---

This recipe builds the page behind `/products/{handle}`: the product with its options and
customizations, stock for the chosen variant, a rail of other products and the tags search engines
read. The examples reuse `NUCLEUS_API_URL`, `NUCLEUS_KEY` and the SDK client `storefront` from
[Getting started](/docs), and the demo store's products.

## Load the store

<EndpointLink id="getStore" /> carries what every page shares: the default currency and locale, the
checkout rules, the SEO title template and which sign-in methods to offer. It changes rarely and may
be cached for a minute (see [Caching](/docs/guides/conventions#caching)), so load it once per
request or keep it in your app's state.

<CodeTabs>

```bash title="curl"
curl "$NUCLEUS_API_URL/storefront/v1/store" \
  -H "Authorization: Bearer $NUCLEUS_KEY"
```

```ts title="fetch"
const shop = await fetch(`${NUCLEUS_API_URL}/storefront/v1/store`, {
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}` },
}).then((response) => response.json());
```

```ts title="SDK"
const shop = await storefront.getStore();
// shop.defaultCurrency, shop.seo.titleTemplate, shop.signInProviders
```

</CodeTabs>

## Fetch the product

<EndpointLink id="getProduct" /> returns the product by its handle, priced for the shopper's market:
pass `country` when you know it (see [Markets](/docs/guides/conventions#markets-and-country)). The
response holds everything the page renders: `title`, `descriptionHtml`, `media`, `options`,
`variants`, `customizations`, `attributes`, `related`, `seo` and `jsonld`.

<CodeTabs>

```bash title="curl"
curl "$NUCLEUS_API_URL/storefront/v1/products/aurora-oval-solitaire?country=US" \
  -H "Authorization: Bearer $NUCLEUS_KEY"
```

```ts title="fetch"
const solitaire = await fetch(
  `${NUCLEUS_API_URL}/storefront/v1/products/aurora-oval-solitaire?country=US`,
  { headers: { Authorization: `Bearer ${NUCLEUS_KEY}` } },
).then((response) => response.json());
```

```ts title="SDK"
const solitaire = await storefront.getProduct(
  { handle: 'aurora-oval-solitaire' },
  { country: 'US' },
);
```

</CodeTabs>

### When the handle is unknown

An unknown or unpublished handle answers `404 NOT_FOUND`. Before you render your 404 page, ask

<EndpointLink id="lookupRedirect" /> whether the merchant moved that URL. It answers the target and
the status to redirect with, or `404` when nothing matches.

<CodeTabs>

```bash title="curl"
curl "$NUCLEUS_API_URL/storefront/v1/redirects/lookup?path=/products/aurora-oval" \
  -H "Authorization: Bearer $NUCLEUS_KEY"
# { "toPath": "/products/aurora-oval-solitaire", "statusCode": 301 }
```

```ts title="fetch"
const lookup = await fetch(
  `${NUCLEUS_API_URL}/storefront/v1/redirects/lookup?path=${encodeURIComponent('/products/aurora-oval')}`,
  { headers: { Authorization: `Bearer ${NUCLEUS_KEY}` } },
);
const redirect = lookup.ok ? await lookup.json() : null;
```

```ts title="SDK"
import { StorefrontApiError } from '@nucleus-commerce/storefront-sdk';

async function findRedirect(path: string) {
  try {
    return await storefront.lookupRedirect({ path });
  } catch (error) {
    if (error instanceof StorefrontApiError && error.code === 'NOT_FOUND') return null;
    throw error;
  }
}

const redirect = await findRedirect('/products/aurora-oval');
// { toPath: '/products/aurora-oval-solitaire', statusCode: 301 }
```

</CodeTabs>

## Pick a variant

`options` lists what the shopper chooses, each with its `values` (`value` is the key, `label` is
what you show). Every variant maps option keys to values in `optionValues` and has its own
`price`, `compareAt` and `isAvailable`. Match the shopper's choices against `optionValues` to find
the variant to show and later add to the cart.

```ts title="Choosing a variant"
// solitaire.options:
//   [{ key: 'metal', values: [{ value: '14k_white_gold', label: '14K White Gold' }, ...] },
//    { key: 'carat_weight', values: [{ value: '1.5', label: '1.50 ct' }, ...] }]
const chosen: Record<string, string> = { metal: '14k_white_gold', carat_weight: '1.5' };

const variant = solitaire.variants.find((candidate) =>
  Object.entries(chosen).every(([key, value]) => candidate.optionValues[key] === value),
);
// variant.price -> { amount: 311850, currency: 'USD' }
```

### Customizations

`customizations` are inputs the shopper fills in for this product: a ring size, an engraving, a
gift message. Render a field per entry: `inputType` is `text` or `select` (with `options`),
`isRequired`, `maxLength` and `pattern` are the rules the API enforces, and `helpText` explains
them. The values travel with the cart line, as shown in
[Cart to checkout](/docs/guides/cart-to-checkout); a missing required value is refused there with
`422 INVALID_INPUT` and `meta.missing` naming the key.

## Show stock

<EndpointLink id="getProductAvailability" /> answers stock per variant: `inStock`, `backorder`,
`available` (`null` when the variant does not track stock) and lead times for made-to-order pieces.
It is never cached, so call it from the browser after the page loads rather than baking it into a
cached page.

<CodeTabs>

```bash title="curl"
curl "$NUCLEUS_API_URL/storefront/v1/products/drift-diamond-pendant/availability" \
  -H "Authorization: Bearer $NUCLEUS_KEY"
```

```ts title="fetch"
const stock = await fetch(
  `${NUCLEUS_API_URL}/storefront/v1/products/drift-diamond-pendant/availability`,
  { headers: { Authorization: `Bearer ${NUCLEUS_KEY}` } },
).then((response) => response.json());
```

```ts title="SDK"
const stock = await storefront.getProductAvailability({ handle: 'drift-diamond-pendant' });
const pendantStock = stock.items[0];
// { variantId, trackInventory: true, available: 5, inStock: true, backorder: false, ... }
```

</CodeTabs>

## Add rails

A product page usually ends with other products. There are three sources, from least to most
merchant control:

- `related` on the product: the products the merchant linked to this one.
- A collection: <EndpointLink id="listCollectionProducts" /> pages through its products, for
  example `bestsellers` with `limit=4`.
- A section: the merchant arranges banners and product rails in the admin under a handle, and
  <EndpointLink id="getSection" /> returns them fully resolved, with media and priced product cards,
  so the storefront only decides the layout. Single blocks come from
  <EndpointLink id="getBlock" /> or, several at once, <EndpointLink id="listBlocks" />.

<CodeTabs>

```bash title="curl"
curl "$NUCLEUS_API_URL/storefront/v1/sections/product-page?country=US" \
  -H "Authorization: Bearer $NUCLEUS_KEY"

curl "$NUCLEUS_API_URL/storefront/v1/collections/bestsellers/products?limit=4&country=US" \
  -H "Authorization: Bearer $NUCLEUS_KEY"
```

```ts title="fetch"
const productPage = await fetch(
  `${NUCLEUS_API_URL}/storefront/v1/sections/product-page?country=US`,
  { headers: { Authorization: `Bearer ${NUCLEUS_KEY}` } },
).then((response) => response.json());

const bestsellers = await fetch(
  `${NUCLEUS_API_URL}/storefront/v1/collections/bestsellers/products?limit=4&country=US`,
  { headers: { Authorization: `Bearer ${NUCLEUS_KEY}` } },
).then((response) => response.json());
```

```ts title="SDK"
const productPage = await storefront.getSection({ handle: 'product-page' }, { country: 'US' });
for (const block of productPage.blocks) {
  if (block.rail) {
    // block.rail.header?.title, block.rail.products: cards with handle, title, price, featuredMedia
  }
  if (block.banner) {
    // block.banner.items: one item is a banner, several are a slider
  }
}

const bestsellers = await storefront.listCollectionProducts(
  { handle: 'bestsellers' },
  { limit: 4, country: 'US' },
);
```

</CodeTabs>

Rail products are cards (`handle`, `title`, `price` as a `{ min, max }` range, `featuredMedia`),
the same shape <EndpointLink id="listProducts" /> returns, so one card component serves every
list.

## Search engine tags

The product carries its tags ready to render: `seo` has `title` (already run through the store's
title template), `description`, `canonical`, `robots`, `openGraph` and `twitter`, and `jsonld` is a
schema.org graph to put in a `<script type="application/ld+json">` tag as is.

```tsx title="Head tags (React)"
import type { StorefrontProduct } from '@nucleus-commerce/storefront-sdk';

function ProductHead({ product }: { product: StorefrontProduct }) {
  return (
    <>
      <title>{product.seo.title}</title>
      {product.seo.description ? (
        <meta name="description" content={product.seo.description} />
      ) : null}
      {product.seo.canonical ? <link rel="canonical" href={product.seo.canonical} /> : null}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(product.jsonld) }}
      />
    </>
  );
}
```

## Put it together

One loader for a server-rendered page, with the SDK. The rails are optional: a store without a
`product-page` section still renders the product. Stock is left to the browser.

```ts title="SDK"
async function loadProductPage(handle: string, country: string) {
  // Started first so both requests run at the same time.
  const rails = storefront.getSection({ handle: 'product-page' }, { country }).then(
    (section) => section.blocks,
    () => [],
  );
  try {
    const product = await storefront.getProduct({ handle }, { country });
    return { status: 200 as const, product, rails: await rails };
  } catch (error) {
    if (!(error instanceof StorefrontApiError) || error.code !== 'NOT_FOUND') throw error;
    const moved = await findRedirect(`/products/${handle}`);
    return moved ? { status: moved.statusCode, location: moved.toPath } : { status: 404 as const };
  }
}

const page = await loadProductPage('aurora-oval-solitaire', 'US');
```
